import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Alert,
  Modal,
  Select,
  Group,
  Stack,
  Text,
  Button,
  Textarea,
  Badge,
  Loader,
  ActionIcon,
  Divider,
  Tooltip,
  ThemeIcon,
  Box,
  Checkbox,
  Paper,
  ScrollArea,
} from "@mantine/core";
import {
  IconFlask,
  IconAlertCircle,
  IconCheck,
  IconPlus,
  IconTrash,
  IconHome,
  IconTruckDelivery,
  IconPackages,
} from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  useStainSearch,
  useRequestAdditionalStainsBatch,
} from "../../hooks/useStainRequest";
import type {
  AdditionalStainRequestSurface,
  StainSearchResult,
} from "../../services/stainRequest.service";
import {
  blockTargetKey,
  getNextBlockPreviewNumber,
  renumberStagedBlockDrafts,
  type StagedBlockDraft,
} from "../../services/stagedBlockRequest";
import { createUuid } from "../../utils/createUuid";
import PathologistPanelRequestForm from "./PathologistPanelRequestForm";

// ==================== TYPES ====================

interface Specimen {
  id: string;
  number: string;
  source?: {
    id: string;
    name: string;
  };
  blocks?: Array<{
    id: string;
    number: string;
  }>;
}

interface RequestStainModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber?: string;
  specimens: Specimen[];
  preselectedSpecimenId?: string;
  onSuccess?: () => void;
  surface?: AdditionalStainRequestSurface;
}

/** A block item in the checkbox list */
interface BlockItem {
  specimenId: string;
  blockId?: string;
  stagedBlockKey?: string;
  key: string;
  label: string;
}

/** Grouped specimen → blocks for the hierarchical checkbox tree */
interface SpecimenGroup {
  specimenId: string;
  specimenLabel: string;
  blocks: BlockItem[];
}

/** Each stain card tracks its own stain selection and checked blocks */
interface StainCardState {
  stainId: string | null;
  stain: StainSearchResult | null;
  searchQuery: string;
  selectedBlocks: string[]; // blockKey strings
  selectedSpecimenId: string | null;
}

// ==================== HELPERS ====================

function formatStainDisplay(code: string, description: string): string {
  return code === description ? code : `${code} - ${description}`;
}

function blockKey(specimenId: string, blockId: string): string {
  return blockTargetKey(specimenId, { kind: "existing", blockId });
}

// ==================== PER-CARD STAIN SEARCH ====================

function useCardStainSearch(query: string, enabled: boolean) {
  const [debounced] = useDebouncedValue(query, 300);
  return useStainSearch(debounced, enabled && debounced.length >= 1);
}

// ==================== STAIN SELECTOR ====================

function StainSelector({
  card,
  onChange,
  opened,
}: {
  card: StainCardState;
  onChange: (stain: StainSearchResult | null, query: string) => void;
  opened: boolean;
}) {
  const { data: results, isLoading } = useCardStainSearch(
    card.searchQuery,
    opened
  );

  const options = useMemo(() => {
    const opts =
      results?.map((s) => ({
        value: s.id,
        label: formatStainDisplay(s.code, s.description),
        stain: s,
      })) || [];

    if (card.stain && !opts.find((o) => o.value === card.stain!.id)) {
      opts.unshift({
        value: card.stain.id,
        label: formatStainDisplay(card.stain.code, card.stain.description),
        stain: card.stain,
      });
    }

    return opts;
  }, [results, card.stain]);

  const showBadge = card.stain !== null;
  const showLoader = isLoading && !showBadge && card.searchQuery.length >= 1;

  return (
    <Select
      placeholder="Search stains..."
      searchable
      nothingFoundMessage={
        isLoading
          ? "Searching..."
          : card.searchQuery.length >= 1
          ? "No stains found"
          : "Type to search"
      }
      data={options}
      value={card.stainId}
      onChange={(value) => {
        if (value) {
          const found =
            results?.find((s) => s.id === value) ??
            (card.stain?.id === value ? card.stain : null);
          onChange(found, card.searchQuery);
        } else {
          onChange(null, "");
        }
      }}
      onSearchChange={(q) => onChange(card.stain, q)}
      searchValue={card.searchQuery}
      rightSection={
        showBadge ? (
          <Tooltip
            label={card.stain!.isAvailableInHouse ? "In-house" : "External"}
            withArrow
            position="top"
          >
            <Box
              style={{ display: "flex", marginRight: 20, cursor: "pointer" }}
            >
              <ThemeIcon
                size="sm"
                variant="light"
                color={card.stain!.isAvailableInHouse ? "green" : "orange"}
              >
                {card.stain!.isAvailableInHouse ? (
                  <IconHome size={14} />
                ) : (
                  <IconTruckDelivery size={14} />
                )}
              </ThemeIcon>
            </Box>
          </Tooltip>
        ) : showLoader ? (
          <Loader size={12} />
        ) : null
      }
      rightSectionWidth={showBadge ? 50 : undefined}
      renderOption={({ option }) => {
        const stain = (option as any).stain as StainSearchResult;
        return (
          <Group justify="space-between" w="100%">
            <Group gap="xs">
              <Text fw={600} size="xs">
                {stain.code}
              </Text>
              <Text size="xs" c="dimmed">
                {stain.description}
              </Text>
            </Group>
            <Badge
              size="xs"
              color={stain.isAvailableInHouse ? "green" : "orange"}
              variant="light"
            >
              {stain.isAvailableInHouse ? "In-house" : "External"}
            </Badge>
          </Group>
        );
      }}
      style={{ flex: 1 }}
    />
  );
}

// ==================== STAIN CARD ====================

function StainCard({
  card,
  specimenGroups,
  canRemove,
  opened,
  showErrors,
  onUpdate,
  onRemove,
}: {
  card: StainCardState;
  specimenGroups: SpecimenGroup[];
  canRemove: boolean;
  opened: boolean;
  showErrors: boolean;
  onUpdate: (patch: Partial<StainCardState>) => void;
  onRemove: () => void;
}) {
  const specimenOptions = useMemo(
    () =>
      specimenGroups.map((g) => ({
        value: g.specimenId,
        label: g.specimenLabel,
      })),
    [specimenGroups]
  );

  const activeGroup = specimenGroups.find(
    (g) => g.specimenId === card.selectedSpecimenId
  );

  const visibleBlockKeys = activeGroup
    ? activeGroup.blocks.map((b) => b.key)
    : [];

  const allChecked =
    visibleBlockKeys.length > 0 &&
    visibleBlockKeys.every((k) => card.selectedBlocks.includes(k));
  const someChecked =
    !allChecked &&
    visibleBlockKeys.some((k) => card.selectedBlocks.includes(k));

  const handleSelectAll = () => {
    onUpdate({ selectedBlocks: allChecked ? [] : [...visibleBlockKeys] });
  };

  const handleBlockToggle = (key: string) => {
    const isChecked = card.selectedBlocks.includes(key);
    const next = isChecked
      ? card.selectedBlocks.filter((k) => k !== key)
      : [...card.selectedBlocks, key];
    onUpdate({ selectedBlocks: next });
  };

  const handleSpecimenChange = (specimenId: string | null) => {
    onUpdate({ selectedSpecimenId: specimenId, selectedBlocks: [] });
  };

  const stainError = showErrors && !card.stainId ? "Select a stain" : null;
  const specimenError =
    showErrors && !card.selectedSpecimenId ? "Select a specimen" : null;
  const blocksError =
    showErrors &&
    card.stainId &&
    card.selectedSpecimenId &&
    card.selectedBlocks.length === 0
      ? "Select at least one block"
      : null;

  return (
    <Paper
      withBorder
      p="sm"
      radius="sm"
      style={
        showErrors &&
        (!card.stainId ||
          !card.selectedSpecimenId ||
          card.selectedBlocks.length === 0)
          ? { borderColor: "var(--mantine-color-red-6)" }
          : undefined
      }
    >
      <Stack gap="xs">
        {/* Stain selector row with optional remove button */}
        <Stack gap={4}>
          <Group gap="xs" align="center" wrap="nowrap">
            <StainSelector
              card={card}
              opened={opened}
              onChange={(stain, query) =>
                onUpdate({
                  stain,
                  stainId: stain?.id ?? null,
                  searchQuery: query,
                })
              }
            />
            {canRemove && (
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={onRemove}
                title="Remove stain"
                style={{ flexShrink: 0 }}
              >
                <IconTrash size={14} />
              </ActionIcon>
            )}
          </Group>
          {stainError && (
            <Text size="xs" c="red" pl={4}>
              {stainError}
            </Text>
          )}
        </Stack>

        {/* Specimen selector */}
        {specimenGroups.length === 0 ? (
          <Text size="sm" c="dimmed">
            No blocks available
          </Text>
        ) : (
          <Stack gap={4}>
            <Select
              placeholder="Select specimen..."
              data={specimenOptions}
              value={card.selectedSpecimenId}
              onChange={handleSpecimenChange}
              size="sm"
              error={specimenError}
            />

            {/* Block checkboxes for the selected specimen */}
            {activeGroup && (
              <Stack gap={4} pl={4}>
                <Checkbox
                  label="Select all blocks"
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={handleSelectAll}
                  size="sm"
                  fw={500}
                />
                {activeGroup.blocks.map((item) => {
                  const key = item.key;
                  return (
                    <Box key={key} pl="md">
                      <Checkbox
                        label={item.label}
                        checked={card.selectedBlocks.includes(key)}
                        onChange={() => handleBlockToggle(key)}
                        size="sm"
                      />
                    </Box>
                  );
                })}
                {blocksError && (
                  <Text size="xs" c="red">
                    {blocksError}
                  </Text>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

// ==================== MAIN COMPONENT ====================

export default function RequestStainModal({
  opened,
  onClose,
  caseId,
  caseNumber,
  specimens,
  preselectedSpecimenId,
  onSuccess,
  surface = "pathologist-review",
}: RequestStainModalProps) {
  const [cards, setCards] = useState<StainCardState[]>([]);
  const [remarks, setRemarks] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [requestMode, setRequestMode] = useState<"stains" | "panel" | "blocks">(
    "stains"
  );
  const [stagedBlocks, setStagedBlocks] = useState<StagedBlockDraft[]>([]);
  const [panelSelectedTargetKeys, setPanelSelectedTargetKeys] = useState<
    string[]
  >([]);
  const [blockReturnMode, setBlockReturnMode] = useState<"stains" | "panel">(
    "stains"
  );
  const wasOpenedRef = useRef(false);
  const initializedCaseIdRef = useRef<string | null>(null);

  const batchMutation = useRequestAdditionalStainsBatch(surface);

  const hasAnySpecimens = specimens.length > 0;

  const specimenGroups: SpecimenGroup[] = useMemo(() => {
    const sorted = [...specimens].sort((a, b) =>
      a.number.localeCompare(b.number)
    );
    return sorted.map((specimen) => {
      const sourceName = specimen.source?.name;
      const specimenLabel = sourceName
        ? `Specimen ${specimen.number} — ${sourceName}`
        : `Specimen ${specimen.number}`;
      const existingBlocks = [...(specimen.blocks || [])].sort((a, b) =>
        a.number.localeCompare(b.number)
      );
      const newBlocks = stagedBlocks
        .filter((draft) => draft.specimenId === specimen.id)
        .sort((left, right) => left.draftKey.localeCompare(right.draftKey));
      return {
        specimenId: specimen.id,
        specimenLabel,
        blocks: [
          ...existingBlocks.map((block) => ({
            specimenId: specimen.id,
            blockId: block.id,
            key: blockKey(specimen.id, block.id),
            label: `Block ${block.number}`,
          })),
          ...newBlocks.map((draft) => ({
            specimenId: specimen.id,
            stagedBlockKey: draft.draftKey,
            key: blockTargetKey(specimen.id, {
              kind: "staged",
              draftKey: draft.draftKey,
            }),
            label: `Block ${draft.previewNumber} (new)`,
          })),
        ],
      };
    });
  }, [specimens, stagedBlocks]);

  // Flat list of all block items (for defaults + submit expansion)
  const blockItems: BlockItem[] = useMemo(
    () => specimenGroups.flatMap((g) => g.blocks),
    [specimenGroups]
  );

  const defaultSpecimenId = useMemo((): string | null => {
    if (preselectedSpecimenId) return preselectedSpecimenId;
    if (specimens.length === 1) return specimens[0].id;
    return null;
  }, [preselectedSpecimenId, specimens]);

  const defaultSelectedBlocks = useMemo((): string[] => {
    if (defaultSpecimenId) {
      const spec = specimens.find((s) => s.id === defaultSpecimenId);
      if (spec?.blocks) {
        return spec.blocks.map((b) => blockKey(defaultSpecimenId, b.id));
      }
    }
    return [];
  }, [defaultSpecimenId, specimens]);

  const makeEmptyCard = useCallback(
    (): StainCardState => ({
      stainId: null,
      stain: null,
      searchQuery: "",
      selectedBlocks: [...defaultSelectedBlocks],
      selectedSpecimenId: defaultSpecimenId,
    }),
    [defaultSelectedBlocks, defaultSpecimenId]
  );

  // Initialize only when the modal actually opens (or switches cases). Data
  // refetches may replace the specimens array while the modal is open and must
  // not discard the pathologist's in-progress request.
  useEffect(() => {
    const shouldInitialize =
      opened &&
      (!wasOpenedRef.current || initializedCaseIdRef.current !== caseId);

    if (shouldInitialize) {
      setCards([makeEmptyCard()]);
      setRemarks("");
      setSubmitted(false);
      setRequestMode("stains");
      setStagedBlocks([]);
      setPanelSelectedTargetKeys([]);
      setBlockReturnMode("stains");
    }

    wasOpenedRef.current = opened;
    if (opened) initializedCaseIdRef.current = caseId;
  }, [opened, caseId, makeEmptyCard]);

  const updateCard = useCallback(
    (index: number, patch: Partial<StainCardState>) => {
      setCards((prev) =>
        prev.map((card, cardIndex) =>
          cardIndex === index ? { ...card, ...patch } : card
        )
      );
    },
    []
  );

  const removeCard = useCallback((index: number) => {
    setCards((prev) => prev.filter((_, cardIndex) => cardIndex !== index));
  }, []);

  const addCard = useCallback(() => {
    setCards((prev) => [...prev, makeEmptyCard()]);
  }, [makeEmptyCard]);

  const handleAddBlock = useCallback(
    (specimenId: string) => {
      const specimen = specimens.find((item) => item.id === specimenId);
      if (!specimen) return;
      const stagedForSpecimen = stagedBlocks.filter(
        (draft) => draft.specimenId === specimenId
      );
      if (stagedForSpecimen.length >= 20) {
        notifications.show({
          title: "Block limit reached",
          message: "No more than 20 blocks can be staged for one specimen.",
          color: "red",
        });
        return;
      }
      setStagedBlocks((current) => [
        ...current,
        {
          draftKey: createUuid(),
          specimenId,
          previewNumber: getNextBlockPreviewNumber(
            (specimen.blocks || []).map((block) => block.number),
            stagedForSpecimen.map((draft) => draft.previewNumber)
          ),
        },
      ]);
    },
    [specimens, stagedBlocks]
  );
  const handleRemoveBlock = useCallback(
    (draftKey: string) => {
      setStagedBlocks((current) => {
        const removedDraft = current.find(
          (draft) => draft.draftKey === draftKey
        );
        if (!removedDraft) return current;
        const remaining = current.filter(
          (draft) => draft.draftKey !== draftKey
        );
        const specimen = specimens.find(
          (item) => item.id === removedDraft.specimenId
        );
        return renumberStagedBlockDrafts(
          remaining,
          removedDraft.specimenId,
          (specimen?.blocks || []).map((block) => block.number)
        );
      });
      const stagedKeySuffix = `::staged::${draftKey}`;
      setCards((current) =>
        current.map((card) => ({
          ...card,
          selectedBlocks: card.selectedBlocks.filter(
            (targetKey) => !targetKey.endsWith(stagedKeySuffix)
          ),
        }))
      );
      setPanelSelectedTargetKeys((current) =>
        current.filter((targetKey) => !targetKey.endsWith(stagedKeySuffix))
      );
    },
    [specimens]
  );
  const handleAddBlockClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const specimenId = event.currentTarget.dataset.specimenId;
      if (specimenId) handleAddBlock(specimenId);
    },
    [handleAddBlock]
  );
  const handleRemoveBlockClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const draftKey = event.currentTarget.dataset.draftKey;
      if (draftKey) handleRemoveBlock(draftKey);
    },
    [handleRemoveBlock]
  );

  const handleShowPanelRequest = useCallback(() => {
    const targetSpecimenId =
      preselectedSpecimenId ||
      (specimens.length === 1 ? specimens[0].id : null);
    const defaultTargets =
      specimenGroups
        .find((group) => group.specimenId === targetSpecimenId)
        ?.blocks.filter((block) => block.blockId)
        .map((block) => block.key) || [];
    setPanelSelectedTargetKeys((current) =>
      current.length > 0 ? current : defaultTargets
    );
    setRequestMode("panel");
  }, [preselectedSpecimenId, specimenGroups, specimens]);
  const handleShowStainRequest = useCallback(() => {
    setRequestMode("stains");
  }, []);
  const handleShowBlockManager = useCallback(() => {
    setBlockReturnMode(requestMode === "panel" ? "panel" : "stains");
    setRequestMode("blocks");
  }, [requestMode]);
  const handleReturnFromBlockManager = useCallback(() => {
    setRequestMode(blockReturnMode);
  }, [blockReturnMode]);
  const handlePanelTargetKeysChange = useCallback((targetKeys: string[]) => {
    setPanelSelectedTargetKeys(targetKeys);
  }, []);
  const handlePanelSuccess = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onClose, onSuccess]);

  const isValid = useMemo(
    () =>
      cards.length > 0 &&
      cards.every(
        (c) => c.stainId && c.selectedSpecimenId && c.selectedBlocks.length > 0
      ),
    [cards]
  );

  // Total number of items to submit (sum of blocks per card)
  const totalItems = useMemo(
    () => cards.reduce((sum, c) => sum + c.selectedBlocks.length, 0),
    [cards]
  );

  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    if (!isValid) return;

    // Build blockItem lookup for expanding cards into batch items
    const blockLookup = new Map<string, BlockItem>(
      blockItems.map((item) => [item.key, item])
    );

    // Expand each card into one item per selected block
    const items = cards.flatMap((card) =>
      card.selectedBlocks
        .map((key) => blockLookup.get(key))
        .filter((block): block is BlockItem => block !== undefined)
        .map((block) => ({
          specimenId: block.specimenId,
          ...(block.blockId
            ? { blockId: block.blockId }
            : { stagedBlockKey: block.stagedBlockKey }),
          stainId: card.stainId!,
          providerId: card.stain!.isAvailableInHouse
            ? null
            : card.stain!.defaultProvider?.id || null,
          levels: null,
        }))
    );

    try {
      const result = await batchMutation.mutateAsync({
        caseId,
        data: {
          items,
          stagedBlocks: stagedBlocks.length > 0 ? stagedBlocks : undefined,
          remarks: remarks.trim() || undefined,
        },
      });

      const hasChildCase = result?.childCase !== null;
      const hasSendOuts = result?.sendOuts && result.sendOuts.length > 0;
      const caseLabel = caseNumber ? `case ${caseNumber}` : "this case";

      if (hasChildCase && hasSendOuts) {
        const externalStainCodes = result.sendOuts
          .map((s) => s.stainCode)
          .join(", ");
        notifications.show({
          title: "Additional Stains Requested",
          message: `Additional-stain work for ${caseLabel} was added to the Cutting Queue. Send-outs for ${externalStainCodes} were added to the Send-Outs Queue.`,
          color: "green",
          icon: <IconCheck size={16} />,
          autoClose: 8000,
        });
      } else if (hasChildCase) {
        const stainCodes = cards
          .map((c) => c.stain?.code)
          .filter(Boolean)
          .join(", ");
        notifications.show({
          title: "Additional Stains Requested",
          message: `${items.length} slide${
            items.length > 1 ? "s" : ""
          } (${stainCodes}) requested for ${caseLabel} and added to the Cutting Queue.`,
          color: "green",
          icon: <IconCheck size={16} />,
          autoClose: 6000,
        });
      } else if (hasSendOuts) {
        const externalStainCodes = result.sendOuts
          .map((s) => s.stainCode)
          .join(", ");
        notifications.show({
          title: "Send-Outs Created",
          message: `External stain request(s) for ${externalStainCodes} created. Check the Send-Outs Queue for tracking.`,
          color: "blue",
          icon: <IconTruckDelivery size={16} />,
          autoClose: 6000,
        });
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to request stains",
        color: "red",
        icon: <IconAlertCircle size={16} />,
      });
    }
  }, [
    batchMutation,
    blockItems,
    cards,
    caseId,
    caseNumber,
    isValid,
    onClose,
    onSuccess,
    remarks,
    stagedBlocks,
  ]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconFlask size={20} />
          <Text fw={600}>
            {requestMode === "panel"
              ? "Request Panel"
              : requestMode === "blocks"
              ? "Add blocks"
              : "Request Additional Stains"}
          </Text>
        </Group>
      }
      size="lg"
      centered
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {/* Case number */}
        {caseNumber && (
          <Text size="sm" c="dimmed">
            Case:{" "}
            <Text span fw={600} c="dark">
              {caseNumber}
            </Text>
          </Text>
        )}

        {!hasAnySpecimens ? (
          <Stack align="center" gap="xs" py="xl">
            <IconAlertCircle size={32} color="var(--mantine-color-red-6)" />
            <Text size="sm" c="red" fw={500} ta="center">
              This case has no specimens. Additional stains cannot be requested.
            </Text>
            <Button variant="default" onClick={onClose} mt="md">
              Close
            </Button>
          </Stack>
        ) : requestMode === "blocks" ? (
          <>
            <Alert color="blue" icon={<IconPackages size={18} />}>
              Add blocks to existing specimens. These are staged locally and are
              created only when an individual stain or panel request is
              submitted.
            </Alert>
            <ScrollArea.Autosize mah={420} offsetScrollbars>
              <Stack gap="sm" pr={4}>
                {specimenGroups.map((group) => {
                  const specimen = specimens.find(
                    (item) => item.id === group.specimenId
                  );
                  const stagedForSpecimen = stagedBlocks.filter(
                    (draft) => draft.specimenId === group.specimenId
                  );
                  return (
                    <Paper key={group.specimenId} withBorder p="sm" radius="md">
                      <Group justify="space-between" align="center">
                        <Text fw={600}>{group.specimenLabel}</Text>
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconPlus size={14} />}
                          data-specimen-id={group.specimenId}
                          onClick={handleAddBlockClick}
                          disabled={stagedForSpecimen.length >= 20}
                        >
                          Add block
                        </Button>
                      </Group>
                      <Stack gap={4} mt="xs" pl="md">
                        {(specimen?.blocks || []).map((block) => (
                          <Text key={block.id} size="sm" c="dimmed">
                            Block {block.number}
                          </Text>
                        ))}
                        {stagedForSpecimen.map((draft) => (
                          <Group
                            key={draft.draftKey}
                            justify="space-between"
                            gap="xs"
                          >
                            <Text size="sm">
                              Block {draft.previewNumber} (new)
                            </Text>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              color="red"
                              data-draft-key={draft.draftKey}
                              onClick={handleRemoveBlockClick}
                            >
                              Remove
                            </Button>
                          </Group>
                        ))}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
            <Group justify="space-between">
              <Button variant="default" onClick={handleReturnFromBlockManager}>
                Back to request
              </Button>
              <Button variant="light" onClick={handleShowPanelRequest}>
                Order a Panel Instead
              </Button>
            </Group>
          </>
        ) : requestMode === "panel" ? (
          <PathologistPanelRequestForm
            caseId={caseId}
            specimens={specimens}
            preselectedSpecimenId={preselectedSpecimenId}
            onCancel={handleShowStainRequest}
            onSuccess={handlePanelSuccess}
            surface={surface}
            stagedBlocks={stagedBlocks}
            selectedTargetKeys={panelSelectedTargetKeys}
            onTargetKeysChange={handlePanelTargetKeysChange}
            onAddBlocks={handleShowBlockManager}
          />
        ) : (
          <>
            <Group justify="space-between">
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={handleShowBlockManager}
              >
                Add blocks
              </Button>
              <Button
                variant="light"
                leftSection={<IconPackages size={16} />}
                onClick={handleShowPanelRequest}
              >
                Order a Panel Instead
              </Button>
            </Group>
            {/* Stain cards */}
            <ScrollArea.Autosize mah={420} offsetScrollbars>
              <Stack gap="sm" pr={4}>
                {cards.map((card, idx) => (
                  <StainCard
                    key={idx}
                    card={card}
                    specimenGroups={specimenGroups}
                    canRemove={cards.length > 1}
                    opened={opened}
                    showErrors={submitted}
                    onUpdate={(patch) => updateCard(idx, patch)}
                    onRemove={() => removeCard(idx)}
                  />
                ))}
              </Stack>
            </ScrollArea.Autosize>

            {/* Add another stain */}
            <Group justify="flex-end">
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconPlus size={14} />}
                onClick={addCard}
              >
                Add Another Stain
              </Button>
            </Group>

            {/* Shared remarks */}
            <Textarea
              label="Remarks"
              placeholder="Optional notes for lab staff..."
              value={remarks}
              onChange={(e) => setRemarks(e.currentTarget.value)}
              minRows={2}
              autosize
            />

            <Divider />

            {/* Actions */}
            <Group justify="space-between">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={batchMutation.isPending}
                leftSection={<IconFlask size={16} />}
              >
                Submit Request{totalItems > 1 ? `s (${totalItems})` : ""}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
